'use client'

import { CxpExtendedContactForm } from '@/components/common/cxp/cxp-extended-contact-form'
import { Container } from '@/components/ui/container'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { ContactFormSection as ContactFormSectionType } from '@/types/coolxprint2'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'

const DEFAULT_LOGO_IMAGE = '/image/placeholder-logo-cxp-white.png'

const DEFAULT_SOLUTION_OPTIONS_IDS = [
  'fast_uniform',
  'custom_production',
  'coolgift',
]

const DEFAULT_QUANTITY_OPTIONS_IDS = [
  'range_1_5',
  'range_6_19',
  'range_20_50',
  'range_51_100',
  'range_101_500',
  'range_501_1000',
  'above_1000',
]

const DEFAULT_CONTACT_INFO_TYPES = ['hotline', 'email'] as const
const CONTACT_VALUES = {
  hotline: { value: '1900 272 735', href: 'tel:1900272735' },
  email: { value: 'pod@coolmate.me', href: 'mailto:pod@coolmate.me' },
}

const DEFAULT_SOCIAL_LINKS = [
  {
    platform: 'zalo',
    href: 'http://zalo.me/0373649650',
    image: '/image/May2023/mceclip0_49.png',
    alt: 'zalo-pod',
  },
  {
    platform: 'facebook',
    href: 'https://www.facebook.com/cxpinaotheoyeucau/',
    image: '/image/April2023/mceclip1_8.png',
    alt: 'fb-pod',
  },
  {
    platform: 'youtube',
    href: 'https://www.youtube.com/@coolmate',
    image: '/image/placeholder-youtube.png',
    alt: 'youtube',
  },
  {
    platform: 'instagram',
    href: 'https://www.instagram.com/coolmate.vn/',
    image: '/image/placeholder-instagram.png',
    alt: 'instagram',
  },
]

// Helper to render icon based on contact type
const renderContactIcon = (type: 'hotline' | 'email') => {
  if (type === 'hotline') {
    return (
      <svg viewBox='0 0 24 24' fill='currentColor' className='h-full w-full'>
        <path d='M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z' />
      </svg>
    )
  }
  return (
    <svg viewBox='0 0 24 24' fill='currentColor' className='h-full w-full'>
      <path d='M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z' />
    </svg>
  )
}

interface ContactFormSectionProps {
  data?: ContactFormSectionType
}

export function ContactFormSection({ data }: ContactFormSectionProps) {
  const t = useTranslations('cxp.home.contact_form')
  const logoImage = data?.logo_image || DEFAULT_LOGO_IMAGE
  const description = data?.description || t('description')
  const formHeading = data?.form_heading || t('form_heading')
  const formBadgeText = data?.form_badge_text || t('form_badge_text')

  // Get contact info from API or use default
  const contactInfo = data?.contact_info?.length
    ? data.contact_info
    : DEFAULT_CONTACT_INFO_TYPES.map((type) => ({
        type,
        label: t(`contact_info.${type}`),
        value: CONTACT_VALUES[type].value,
        href: CONTACT_VALUES[type].href,
      }))

  // Get social links from API or use default
  const socialLinks =
    data?.social_links
      ?.filter((link) => link.is_active !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0)) || DEFAULT_SOCIAL_LINKS

  // Get solution options from API or use default
  const solutionOptions = data?.solution_options?.length
    ? data.solution_options.sort((a, b) => a.order - b.order)
    : DEFAULT_SOLUTION_OPTIONS_IDS.map((id, index) => ({
        value: id,
        label: t(`solution_options.${id}`),
        order: index,
      }))

  // Get quantity options from API or use default
  const quantityOptions = data?.quantity_options?.length
    ? data.quantity_options.sort((a, b) => a.order - b.order)
    : DEFAULT_QUANTITY_OPTIONS_IDS.map((id, index) => ({
        value: id,
        label: t(`quantity_options.${id}`),
        order: index,
      }))

  return (
    <div id='contact-form' className='bg-white py-10 md:py-16 lg:py-20'>
      <Container fluid={true}>
        <div className='grid grid-cols-1 gap-6 sm:gap-8 md:gap-10 lg:grid-cols-2 lg:gap-16'>
          {/* Left Side - Contact Info */}
          <div className='flex flex-col space-y-5 rounded-xl bg-[#1a1a1a] p-5 sm:space-y-6 sm:rounded-2xl sm:p-6 md:space-y-8 md:p-8 lg:p-12'>
            {/* Logo */}
            <div className='relative h-12 w-36 sm:h-[60px] sm:w-[180px] md:h-[79px] md:w-[223px]'>
              <Image
                src={ImageHelper.getImageUrl(logoImage)}
                alt='CXP Logo'
                fill
                className='object-contain'
                sizes='(max-width: 640px) 144px, (max-width: 768px) 180px, 223px'
              />
            </div>

            {/* Description */}
            <p className='font-sans text-sm text-white sm:text-base md:text-lg'>
              {description}
            </p>

            <div className='flex flex-1 flex-col justify-end'>
              {/* Contact Info */}
              {contactInfo.length > 0 && (
                <div className='space-y-3 sm:space-y-4 md:space-y-6'>
                  {contactInfo.map((info) => (
                    <div
                      key={info.type}
                      className='flex items-center gap-2.5 sm:gap-3 md:gap-4'
                    >
                      <div className='flex h-9 w-9 items-center justify-center rounded-full bg-neutral-100 text-neutral-700 sm:h-10 sm:w-10 md:h-12 md:w-12'>
                        <div className='h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6'>
                          {renderContactIcon(info.type)}
                        </div>
                      </div>
                      <div>
                        <p className='mb-0.5 font-sans text-[10px] text-neutral-300 sm:mb-1 sm:text-xs md:text-sm'>
                          {info.label}
                        </p>
                        <a
                          href={info.href}
                          className='text-sm font-semibold text-white transition-colors hover:text-[#ff4e00] sm:text-base md:text-lg'
                        >
                          {info.value}
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Social Links */}
            {socialLinks.length > 0 && (
              <div className='flex items-center justify-end gap-2.5 sm:gap-3 md:gap-4'>
                {socialLinks.map((link, index) => (
                  <Link
                    key={link.platform || index}
                    href={link.href}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='flex h-9 w-9 items-center justify-center transition-colors sm:h-10 sm:w-10 md:h-12 md:w-12'
                  >
                    <Image
                      src={ImageHelper.getImageUrl(link.image)}
                      alt={link.alt}
                      width={24}
                      height={24}
                      className='h-full w-full object-contain'
                    />
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Right Side - Contact Form */}
          <CxpExtendedContactForm
            solutionOptions={solutionOptions}
            quantityOptions={quantityOptions}
            formHeading={formHeading}
            formBadgeText={formBadgeText}
          />
        </div>
      </Container>
    </div>
  )
}
